import { useEffect, useRef, useState } from "react";
import { readLoadingConstraintRecovery } from "../utils/loadingConstraintSourceChanges.js";

export default function useLoadingConstraintRecovery({
  actionsDisabled, details, isBusy, onRefresh, onRecoveryOpenChange, previewRevision, scopeKey,
}) {
  const [activeTruckId, setActiveTruckId] = useState("");
  const [recovery, setRecovery] = useState(null);
  const inFlight = useRef(false);
  const triggerRef = useRef(null);
  const currentScope = useRef(scopeKey);
  currentScope.current = scopeKey;

  useEffect(() => {
    onRecoveryOpenChange?.(Boolean(recovery));
    return () => onRecoveryOpenChange?.(false);
  }, [recovery, onRecoveryOpenChange]);

  useEffect(() => {
    if (recovery && (
      `${recovery.dispatchDate}|${recovery.dispatchPeriod}` !== scopeKey
      || recovery.previewRevision !== previewRevision
      || !details.some((detail) => detail.truckId === recovery.truckId
        && detail.changeRevision === recovery.changeRevision)
    )) setRecovery(null);
  }, [details, previewRevision, recovery, scopeKey]);

  const closeRecovery = () => {
    if (inFlight.current) return;
    setRecovery(null);
    requestAnimationFrame(() => {
      if (triggerRef.current?.isConnected) triggerRef.current.focus();
    });
  };

  const refreshTruck = async (detail) => {
    if (typeof onRefresh !== "function" || inFlight.current || isBusy || actionsDisabled) return;
    const requestScope = scopeKey;
    inFlight.current = true;
    triggerRef.current = document.activeElement;
    setActiveTruckId(detail.truckId);
    try {
      const payload = await onRefresh(detail);
      const proposal = readLoadingConstraintRecovery(payload);
      if (proposal && currentScope.current === requestScope
        && `${proposal.dispatchDate}|${proposal.dispatchPeriod}` === requestScope) {
        setRecovery(proposal);
      }
    } finally {
      inFlight.current = false;
      setActiveTruckId("");
    }
  };

  const confirmRecovery = async () => {
    if (!recovery || inFlight.current || isBusy || actionsDisabled
      || `${recovery.dispatchDate}|${recovery.dispatchPeriod}` !== currentScope.current
      || recovery.previewRevision !== previewRevision) return;
    inFlight.current = true;
    setActiveTruckId(recovery.truckId);
    try {
      // Send the exact revision shown in the dialog; confirmation is never retried
      // against a different preview or a newly calculated proposal.
      await onRefresh(recovery);
    } finally {
      inFlight.current = false;
      setActiveTruckId("");
      closeRecovery();
    }
  };

  return { activeTruckId, recovery, refreshTruck, closeRecovery, confirmRecovery };
}
